import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Scale,
  Send,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  HelpCircle,
  Users,
  Shield,
  Clock,
  BarChart3,
  History,
  Zap,
  Brain,
  Lock
} from 'lucide-react';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';

const TribunalIA = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [question, setQuestion] = useState('');
  const [category, setCategory] = useState('general');
  const [requireUnanimous, setRequireUnanimous] = useState(false);
  const [verdict, setVerdict] = useState(null);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);

  const judges = [
    { name: 'Mistral Small 3.2', provider: 'Mistral • Open source', emoji: '', color: 'from-orange-500 to-amber-600', role: 'Analyste Principal' },
    { name: 'Llama 4 Scout', provider: 'Meta • Open source', emoji: '', color: 'from-blue-500 to-indigo-600', role: 'Vérificateur' },
    { name: 'DeepSeek R1', provider: 'DeepSeek • Open source', emoji: '', color: 'from-cyan-500 to-teal-600', role: 'Arbitre' }
  ];

  const categories = [
    { value: 'general', label: 'Général', icon: '💬' },
    { value: 'security', label: 'Sécurité', icon: '🔒' },
    { value: 'finance', label: 'Finance/Paie', icon: '💰' },
    { value: 'operations', label: 'Opérations', icon: '⚙️' },
    { value: 'hr', label: 'RH/Personnel', icon: '👥' },
    { value: 'legal', label: 'Juridique', icon: '⚖️' }
  ];

  useEffect(() => {
    fetchStats();
    fetchHistory();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/tribunal/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await api.get('/tribunal/verdicts?limit=5');
      setHistory(response.data || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const submitToTribunal = async () => {
    if (!question.trim()) {
      toast({ title: 'Erreur', description: 'Veuillez entrer une question', variant: 'destructive' });
      return;
    }

    setLoading(true);
    setVerdict(null);

    try {
      const response = await api.post('/tribunal/judge', {
        question,
        category,
        require_unanimous: requireUnanimous
      });

      setVerdict(response.data.verdict);
      fetchStats();
      fetchHistory();

      if (response.data.verdict.consensus) {
        toast({ title: '⚖️ Verdict rendu', description: response.data.summary });
      } else {
        toast({ title: '⚠️ Décision disputée', description: 'Arbitrage humain recommandé', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de contacter le tribunal', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const getDecisionIcon = (decision) => {
    switch (decision) {
      case 'approve': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'reject': return <XCircle className="w-5 h-5 text-red-500" />;
      case 'need_info': return <HelpCircle className="w-5 h-5 text-yellow-500" />;
      default: return <AlertTriangle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getVerdictStyle = (decision) => {
    switch (decision) {
      case 'approved': return 'bg-green-900/30 border-green-500 text-green-400';
      case 'rejected': return 'bg-red-900/30 border-red-500 text-red-400';
      case 'disputed': return 'bg-yellow-900/30 border-yellow-500 text-yellow-400';
      default: return 'bg-gray-900/30 border-gray-500 text-gray-400';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 text-white p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl">
            <Scale className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
              Tribunal IA
            </h1>
            <p className="text-slate-400">3 IA indépendantes pour des décisions sécurisées</p>
          </div>
        </div>

        {/* Judges Display */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {judges.map((judge, i) => (
            <Card key={i} className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${judge.color}`}>
                    <Brain className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-white">{judge.emoji} {judge.name}</p>
                    <p className="text-xs text-slate-400">{judge.provider} • {judge.role}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Panel */}
        <div className="lg:col-span-2 space-y-6">
          {/* Input Card */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-indigo-400" />
                Soumettre au Tribunal
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Category Selection */}
              <div>
                <label className="text-sm text-slate-400 mb-2 block">Catégorie</label>
                <div className="flex flex-wrap gap-2">
                  {categories.map((cat) => (
                    <Button
                      key={cat.value}
                      variant={category === cat.value ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCategory(cat.value)}
                      className={category === cat.value ? 'bg-indigo-600' : 'border-slate-600'}
                    >
                      {cat.icon} {cat.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Question Input */}
              <div>
                <label className="text-sm text-slate-400 mb-2 block">Question / Demande</label>
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Décrivez votre question ou demande de validation..."
                  className="w-full h-32 bg-slate-700 border-slate-600 rounded-lg p-3 text-white placeholder:text-slate-500 resize-none"
                />
              </div>

              {/* Options */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={requireUnanimous}
                    onChange={(e) => setRequireUnanimous(e.target.checked)}
                    className="rounded border-slate-600"
                  />
                  <Lock className="w-4 h-4" />
                  Exiger l'unanimité (3/3)
                </label>

                <Button
                  onClick={submitToTribunal}
                  disabled={loading || !question.trim()}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Délibération...
                    </>
                  ) : (
                    <>
                      <Scale className="w-4 h-4 mr-2" />
                      Soumettre au Tribunal
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Verdict Display */}
          {verdict && (
            <Card className={`border-2 ${getVerdictStyle(verdict.final_decision)}`}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Scale className="w-5 h-5" />
                    Verdict du Tribunal
                  </span>
                  <span className="text-sm font-normal flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {verdict.totalTimeMs}ms
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Final Decision */}
                <div className={`p-4 rounded-lg border ${getVerdictStyle(verdict.final_decision)}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-lg font-bold">
                      {verdict.final_decision === 'approved' && '✅ APPROUVÉ'}
                      {verdict.final_decision === 'rejected' && '❌ REJETÉ'}
                      {verdict.final_decision === 'disputed' && '⚠️ DISPUTÉ'}
                      {verdict.final_decision === 'need_info' && '❓ INFOS REQUISES'}
                    </span>
                    <span className="text-2xl font-bold">{Math.round(verdict.confidence_score * 100)}%</span>
                  </div>
                  <p className="text-sm opacity-80">
                    {verdict.unanimous ? '🌟 Unanimité' : `Vote: ${verdict.votes?.approve || 0}✅ ${verdict.votes?.reject || 0}❌ ${verdict.votes?.need_info || 0}❓`}
                  </p>
                </div>

                {/* Individual Judge Responses */}
                <div className="space-y-3">
                  <p className="text-sm text-slate-400 font-medium">Avis des juges :</p>
                  {verdict.judges?.map((judge, i) => (
                    <div key={i} className="bg-slate-700/50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span>{judges[i]?.emoji}</span>
                          <span className="font-medium text-white">{judge.name}</span>
                          <span className="text-xs text-slate-400">({judge.role})</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {getDecisionIcon(judge.decision)}
                          <span className="text-sm text-slate-300">{Math.round((judge.confidence || 0) * 100)}%</span>
                        </div>
                      </div>
                      <p className="text-sm text-slate-300 line-clamp-3">
                        {judge.reasoning || judge.response?.substring(0, 200)}...
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Stats */}
          {stats && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-indigo-400" />
                  Statistiques
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-700/50 rounded p-3 text-center">
                    <p className="text-2xl font-bold text-white">{stats.total}</p>
                    <p className="text-xs text-slate-400">Total verdicts</p>
                  </div>
                  <div className="bg-slate-700/50 rounded p-3 text-center">
                    <p className="text-2xl font-bold text-green-400">{stats.consensusRate}%</p>
                    <p className="text-xs text-slate-400">Taux consensus</p>
                  </div>
                  <div className="bg-green-900/30 rounded p-3 text-center">
                    <p className="text-xl font-bold text-green-400">{stats.approved}</p>
                    <p className="text-xs text-green-300">Approuvés</p>
                  </div>
                  <div className="bg-red-900/30 rounded p-3 text-center">
                    <p className="text-xl font-bold text-red-400">{stats.rejected}</p>
                    <p className="text-xs text-red-300">Rejetés</p>
                  </div>
                </div>
                {stats.disputed > 0 && (
                  <div className="bg-yellow-900/30 rounded p-2 text-center">
                    <p className="text-sm text-yellow-400">⚠️ {stats.disputed} décision(s) disputée(s)</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Why 3 AIs */}
          <Card className="bg-gradient-to-br from-indigo-900/50 to-purple-900/50 border-indigo-700">
            <CardContent className="pt-6">
              <h3 className="font-bold text-white mb-3 flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-400" />
                Pourquoi 3 IA ?
              </h3>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-start gap-2">
                  <span className="text-red-400">❌</span>
                  <span><strong>1 IA</strong> = peut dévier/se tromper</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-400">⚠️</span>
                  <span><strong>2 IA</strong> = conflit d'intérêt possible</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400">✅</span>
                  <span><strong>3 IA</strong> = SÉCURITÉ (vote majoritaire)</span>
                </li>
              </ul>
              <div className="mt-4 pt-4 border-t border-slate-700">
                <p className="text-xs text-slate-400">
                  3 entreprises différentes = vraie indépendance
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Recent History */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <History className="w-5 h-5 text-indigo-400" />
                Historique récent
              </CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">Aucun verdict encore</p>
              ) : (
                <div className="space-y-2">
                  {history.map((v, i) => (
                    <div key={i} className="bg-slate-700/50 rounded p-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="truncate flex-1 text-slate-300">{v.question?.substring(0, 30)}...</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          v.final_decision === 'approved' ? 'bg-green-900/50 text-green-400' :
                          v.final_decision === 'rejected' ? 'bg-red-900/50 text-red-400' :
                          'bg-yellow-900/50 text-yellow-400'
                        }`}>
                          {v.final_decision === 'approved' ? '✅' : v.final_decision === 'rejected' ? '❌' : '⚠️'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TribunalIA;
